// client/src/contexts/AuthContext.js
// ✅ MOBILE FIX: logout-mode + logout-guard stored in BOTH localStorage + sessionStorage
// ✅ MOBILE FIX: "recent logout lock" prevents bounce back to dashboard on restore
// ✅ FIX: auth-check does NOT use cookies (credentials: "omit")
// ✅ StrictMode-safe + role-enforced (client only)
//
// ✅ NEW (THIS UPDATE):
// - Client-only token key (clientAuthToken) to prevent superadmin/client collisions
// - Persisted client auth state (user + advertisers) so pages don't lose access on refresh
// - Backward compatible with legacy authToken/token keys
//
// ✅ CRA-SAFE:
// - NO import.meta usage (CRA parser can choke even with optional chaining)

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// -----------------------------
// ✅ API base hardening (CRA-safe)
// -----------------------------
const normalizeBase = (base) => {
  if (!base) return "";
  let b = String(base).trim();
  b = b.replace(/\/+$/, "");
  if (b.toLowerCase().endsWith("/api")) b = b.slice(0, -4);
  return b;
};

const resolveApiBase = () => {
  // ✅ CRA: only REACT_APP_* is safe to reference directly
  const envBase = process.env.REACT_APP_BACKEND_URL || "";
  const cleaned = normalizeBase(envBase);
  return cleaned || "https://cphere-backend.onrender.com";
};

const API_BASE = resolveApiBase();

const apiUrl = (path) => {
  const p = String(path || "").trim();
  if (!p) return `${API_BASE}/api`;
  if (p.startsWith("http://") || p.startsWith("https://")) return p;

  if (p.startsWith("/api/")) return `${API_BASE}${p}`;
  if (p.startsWith("api/")) return `${API_BASE}/${p}`;
  return `${API_BASE}/api${p.startsWith("/") ? "" : "/"}${p}`;
};

// -----------------------------
// ✅ Context
// -----------------------------
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// -----------------------------
// ✅ Defaults
// -----------------------------
const AUTH_CHECK_TIMEOUT_MS = Number(
  process.env.REACT_APP_AUTH_CHECK_TIMEOUT_MS || 20000
);

const AUTH_CHECK_ENDPOINT =
  process.env.REACT_APP_AUTH_CHECK_ENDPOINT || "/api/auth/client-auth-check";

// ✅ Keys
const AUTH_TRANSITION_KEY = "cortexAuthTransition";
const LOGOUT_IN_PROGRESS_KEY = "cortexLogoutInProgress";
const LOGOUT_MODE_KEY = "cortexLogoutMode";
const PASSWORD_CHANGE_SUCCESS_KEY = "cpherePasswordChangeSuccess";
const PASSWORD_CHANGE_EXPIRED_KEY = "cpherePasswordChangeExpired";
const PASSWORD_CHANGE_PENDING_RELOAD = "pending-reload";
const PASSWORD_CHANGE_ARMED_RELOAD = "armed-reload";

// ✅ NEW: recent logout lock (prevents mobile bounce loop)
const LAST_LOGOUT_TS_KEY = "cortexLastLogoutTs";
const RECENT_LOGOUT_LOCK_MS = 30000; // 30s

// ✅ Advertiser selection keys
const KEY_SELECTED_ADV_ID = "selectedAdvertiserId";
const KEY_SELECTED_ADV_NAME = "selectedAdvertiserName";
const LEGACY_SELECTED_ADV_NAME = "selectedAdvertiser";

// ✅ NEW: client-only auth keys
const CLIENT_TOKEN_KEY = "clientAuthToken"; // primary token key for client portal
const CLIENT_AUTH_STATE_KEY = "cortexClientAuthState"; // persisted auth blob
const CLIENT_PORTAL_ROLES = new Set(["client", "creative"]);

// ✅ Legacy email support
const getStoredEmail = () =>
  localStorage.getItem("email") || localStorage.getItem("userEmail") || "";

const normalizeRole = (val) =>
  String(val || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]/g, "");

// ✅ Determine explicit ok from JSON (optional)
const computeOkFromJson = (json) => {
  if (!json || typeof json !== "object") return null;

  const explicitKeys = ["ok", "authorized", "authenticated", "isAuthenticated"];
  for (const k of explicitKeys) {
    if (typeof json[k] === "boolean") return json[k];
  }

  if (typeof json.status === "string") {
    const s = json.status.toLowerCase();
    if (s === "ok" || s === "success" || s === "authorized") return true;
    if (s === "error" || s === "unauthorized" || s === "forbidden") return false;
  }

  if (typeof json.error === "string" && json.error.trim()) return false;

  return null;
};

// ✅ Fallback authorization: allow 200 only if it contains identity-ish fields
const looksLikeAuthorizedPayload = (json) => {
  if (!json || typeof json !== "object") return false;

  if (typeof json.error === "string" && json.error.trim()) return false;

  const hasEmail = typeof json.email === "string" && json.email.trim().length > 0;
  const hasRole = typeof json.role === "string" && json.role.trim().length > 0;

  const user = json.user;
  const hasUserId =
    (user && (user.id || user._id)) || json.id || json.userId || json.accountId;

  return Boolean(hasEmail || hasRole || hasUserId);
};

// ✅ Helpers (safe JSON)
const safeJsonParse = (raw, fallback) => {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

// ✅ MOBILE SAFE: check BOTH storages
const getFlagBoth = (key) => {
  try {
    if (localStorage.getItem(key) === "1") return true;
  } catch {}
  try {
    if (sessionStorage.getItem(key) === "1") return true;
  } catch {}
  return false;
};

const setFlagBoth = (key) => {
  try {
    localStorage.setItem(key, "1");
  } catch {}
  try {
    sessionStorage.setItem(key, "1");
  } catch {}
};

const clearFlagBoth = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {}
  try {
    sessionStorage.removeItem(key);
  } catch {}
};

const isLoggingOut = () => getFlagBoth(LOGOUT_IN_PROGRESS_KEY);
const setLogoutGuard = () => setFlagBoth(LOGOUT_IN_PROGRESS_KEY);
const clearLogoutGuard = () => clearFlagBoth(LOGOUT_IN_PROGRESS_KEY);

// ✅ Logout-mode: store BOTH (mobile PWA/sessionStorage can be flaky)
const isLogoutMode = () => getFlagBoth(LOGOUT_MODE_KEY);
const setLogoutMode = () => setFlagBoth(LOGOUT_MODE_KEY);
const clearLogoutMode = () => clearFlagBoth(LOGOUT_MODE_KEY);

// ✅ Recent logout lock
const setRecentLogoutLock = () => {
  const ts = String(Date.now());
  try {
    localStorage.setItem(LAST_LOGOUT_TS_KEY, ts);
  } catch {}
  try {
    sessionStorage.setItem(LAST_LOGOUT_TS_KEY, ts);
  } catch {}
};

const getLastLogoutTs = () => {
  let v = "";
  try {
    v = localStorage.getItem(LAST_LOGOUT_TS_KEY) || "";
  } catch {}
  if (!v) {
    try {
      v = sessionStorage.getItem(LAST_LOGOUT_TS_KEY) || "";
    } catch {}
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const isInRecentLogoutLock = () => {
  const ts = getLastLogoutTs();
  if (!ts) return false;
  return Date.now() - ts < RECENT_LOGOUT_LOCK_MS;
};

const clearRecentLogoutLock = () => {
  try {
    localStorage.removeItem(LAST_LOGOUT_TS_KEY);
  } catch {}
  try {
    sessionStorage.removeItem(LAST_LOGOUT_TS_KEY);
  } catch {}
};

// -----------------------------
// ✅ Token resolution (client-first, legacy fallback)
// -----------------------------
const getStoredTokenClientFirst = () => {
  return (
    localStorage.getItem(CLIENT_TOKEN_KEY) ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    ""
  );
};

// -----------------------------
// ✅ Extract user + advertisers from auth-check payloads
// -----------------------------
const extractUserFromPayload = (json) => {
  if (!json || typeof json !== "object") return null;
  return json.user || json.data?.user || json.account || null;
};

const extractAdvertisersFromPayload = (json) => {
  if (!json || typeof json !== "object") return [];
  const u = extractUserFromPayload(json);

  const fromObjects = Array.isArray(json?.advertiserObjects)
    ? json.advertiserObjects
    : Array.isArray(u?.advertiserObjects)
    ? u.advertiserObjects
    : Array.isArray(json?.advertisersExpanded)
    ? json.advertisersExpanded
    : Array.isArray(u?.advertisersExpanded)
    ? u.advertisersExpanded
    : null;
  if (fromObjects?.length) return fromObjects;

  const ids = Array.isArray(json?.advertiserIds)
    ? json.advertiserIds
    : Array.isArray(json?.advertiser_ids)
    ? json.advertiser_ids
    : Array.isArray(u?.advertiserIds)
    ? u.advertiserIds
    : Array.isArray(u?.advertiser_ids)
    ? u.advertiser_ids
    : [];
  const fromUser = Array.isArray(u?.advertisers) ? u.advertisers : null;
  const fromTop = Array.isArray(json?.advertisers) ? json.advertisers : null;
  const fromAccounts = Array.isArray(json?.accounts) ? json.accounts : null;

  const out = fromUser || fromTop || fromAccounts || [];
  if (Array.isArray(out) && out.length && ids.length) {
    return out.map((name, index) => ({ id: ids[index] ?? name, name }));
  }
  return Array.isArray(out) ? out : [];
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // ✅ NEW: hydrate from persisted client auth state if present
  const savedAuth = useMemo(() => {
    const raw = localStorage.getItem(CLIENT_AUTH_STATE_KEY);
    return safeJsonParse(raw, null);
  }, []);

  const [authToken, setAuthToken] = useState(() => {
    const t = getStoredTokenClientFirst();
    return t;
  });

  const [email, setEmail] = useState(() => {
    const fromSaved = String(savedAuth?.email || "").trim();
    return fromSaved || getStoredEmail();
  });

  const [role, setRole] = useState(() => {
    const fromSaved = normalizeRole(savedAuth?.role || "");
    const fromStorage = normalizeRole(localStorage.getItem("role") || "");
    return (fromSaved || fromStorage || "client") || "client";
  });

  // ✅ NEW: persist user + advertisers (so dropdown/access stays stable)
  const [user, setUser] = useState(() => savedAuth?.user || null);
  const [advertisers, setAdvertisers] = useState(() => {
    const a = savedAuth?.advertisers;
    return Array.isArray(a) ? a : [];
  });

  const [authError, setAuthError] = useState("");

  const checkedKeyRef = useRef(null);
  const inflightRef = useRef({ key: null, promise: null });
  const authGenerationRef = useRef(0);

  const setUnauthed = useCallback(() => {
    setIsAuthenticated(false);
    setAuthError("");
    try {
      sessionStorage.removeItem(AUTH_TRANSITION_KEY);
    } catch {}
  }, []);

  const clearAuthStorage = useCallback(() => {
    const keys = [
      // ✅ client-first
      CLIENT_TOKEN_KEY,
      CLIENT_AUTH_STATE_KEY,

      // legacy
      "authToken",
      "token",
      "email",
      "userEmail",
      "role",

      // advertiser selection
      KEY_SELECTED_ADV_ID,
      KEY_SELECTED_ADV_NAME,
      LEGACY_SELECTED_ADV_NAME,
    ];

    keys.forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch {}
      try {
        sessionStorage.removeItem(k);
      } catch {}
    });
  }, []);

  // ✅ best-effort server logout (clears httpOnly cookies if backend supports it)
  const serverLogout = useCallback(async () => {
    const candidates = ["/api/auth/logout", "/api/logout", "/auth/logout"].map((p) =>
      apiUrl(p)
    );

    for (const url of candidates) {
      try {
        const res = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          cache: "no-store",
        });
        if (res.ok) return true;
      } catch {}
    }

    for (const url of candidates) {
      try {
        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        });
        if (res.ok) return true;
      } catch {}
    }

    return false;
  }, []);

  const logout = useCallback(() => {
    authGenerationRef.current += 1;
    const logoutGeneration = authGenerationRef.current;
    // ✅ lock first so mobile can't bounce back
    setRecentLogoutLock();

    // ✅ Enter logout-mode FIRST
    setLogoutMode();
    setLogoutGuard();

    try {
      sessionStorage.removeItem(AUTH_TRANSITION_KEY);
    } catch {}

    // ✅ clear auth immediately
    clearAuthStorage();

    // ✅ update state immediately
    setIsAuthenticating(false);
    setIsAuthenticated(false);
    setAuthToken("");
    setEmail("");
    setRole("client");

    // ✅ NEW
    setUser(null);
    setAdvertisers([]);

    setAuthError("");

    checkedKeyRef.current = null;
    inflightRef.current = { key: null, promise: null };

    setUnauthed();

    // ✅ do not block UI
    try {
      serverLogout();
    } catch {}

    // ✅ MOBILE: single hard redirect prevents "seizure" rerender loops
    try {
      const p =
        String(window.location?.pathname || "")
          .toLowerCase()
          .trim()
          .replace(/\/+$/, "") || "/";
      let hasPendingPasswordNotice = false;
      try {
        hasPendingPasswordNotice = [PASSWORD_CHANGE_SUCCESS_KEY, PASSWORD_CHANGE_EXPIRED_KEY]
          .some((key) => sessionStorage.getItem(key) === PASSWORD_CHANGE_PENDING_RELOAD);
      } catch {}

      // A late password response can arrive after Login is already mounted.
      // Reload that page once when a notice is pending so it is observable and
      // consumed with the same handoff as the normal Settings transition.
      if (p !== "/login" || hasPendingPasswordNotice) {
        setTimeout(() => {
          // A replacement login may complete while this redirect is queued.
          // In that case, leave the newly authenticated session in place.
          if (authGenerationRef.current !== logoutGeneration) return;

          // Arm the one-time password notice handoff immediately before
          // navigation. The SPA login rendered by logout mode must leave the
          // pending marker untouched until this hard navigation occurs.
          try {
            for (const key of [PASSWORD_CHANGE_SUCCESS_KEY, PASSWORD_CHANGE_EXPIRED_KEY]) {
              if (sessionStorage.getItem(key) === PASSWORD_CHANGE_PENDING_RELOAD) {
                sessionStorage.setItem(key, PASSWORD_CHANGE_ARMED_RELOAD);
              }
            }
          } catch {}
          try {
            window.location.replace("/login?logout=1");
          } catch {}
        }, 50);
      }
    } catch {}
  }, [clearAuthStorage, serverLogout, setUnauthed]);

  const setSession = useCallback(
    ({ token, email: em, role: r, user: u, advertisers: advs } = {}) => {
      const t = String(token || "").trim();
      const e = String(em || "").trim();
      const rr = normalizeRole(r || "client") || "client";
      const nextUser = u && typeof u === "object" ? u : null;
      const nextAdvertisers = Array.isArray(advs) ? advs : [];
      const sessionIsAuthorized = Boolean(t && CLIENT_PORTAL_ROLES.has(rr));

      if (sessionIsAuthorized) {
        // Invalidate delayed logout work and notices that belong to the
        // session being replaced before installing the new session.
        authGenerationRef.current += 1;
        try {
          sessionStorage.removeItem(PASSWORD_CHANGE_SUCCESS_KEY);
          sessionStorage.removeItem(PASSWORD_CHANGE_EXPIRED_KEY);
        } catch {}
      }

      // ✅ Store client token in client key
      if (t) localStorage.setItem(CLIENT_TOKEN_KEY, t);

      // ✅ Backward-compatible: also set authToken (optional safety) but clear legacy "token"
      if (t) localStorage.setItem("authToken", t);
      try {
        localStorage.removeItem("token");
      } catch {}

      if (e) {
        localStorage.setItem("email", e);
        localStorage.setItem("userEmail", e);
      }
      if (rr) localStorage.setItem("role", rr);

      setAuthToken(t);
      setEmail(e);
      setRole(rr);

      // ✅ NEW: persist user + advertisers
      setUser(nextUser);
      if (nextUser) {
        try {
          if (
            typeof nextUser.preferredDisplayName === "string" &&
            nextUser.preferredDisplayName.trim()
          ) {
            localStorage.setItem(
              "preferredDisplayName",
              nextUser.preferredDisplayName.trim()
            );
          } else {
            localStorage.removeItem("preferredDisplayName");
          }
        } catch {}
      } else {
        try {
          localStorage.removeItem("preferredDisplayName");
        } catch {}
      }
      setAdvertisers(nextAdvertisers);

      setIsAuthenticated(sessionIsAuthorized);
      setIsAuthenticating(false);
      setAuthError("");

      if (sessionIsAuthorized) {
        try {
          localStorage.setItem(
            CLIENT_AUTH_STATE_KEY,
            JSON.stringify({
              isAuthenticated: true,
              token: t,
              email: e,
              role: rr,
              user: nextUser,
              advertisers: nextAdvertisers,
              updatedAt: new Date().toISOString(),
            })
          );
        } catch {}
      } else {
        try {
          localStorage.removeItem(CLIENT_AUTH_STATE_KEY);
        } catch {}
      }

      checkedKeyRef.current = null;
      inflightRef.current = { key: null, promise: null };

      // ✅ clear locks after successful login
      clearLogoutGuard();
      clearLogoutMode();
      clearRecentLogoutLock();

      try {
        sessionStorage.removeItem(AUTH_TRANSITION_KEY);
      } catch {}
    },
    []
  );

  const headersForToken = useCallback((token) => {
    const h = { "Content-Type": "application/json" };
    const t = String(token || "").trim();
    if (t) h.Authorization = `Bearer ${t}`;
    return h;
  }, []);

  const persistClientAuthState = useCallback(
    ({ ok, token, nextEmail, nextRole, nextUser, nextAdvertisers }) => {
      if (!ok) {
        try {
          localStorage.removeItem(CLIENT_AUTH_STATE_KEY);
        } catch {}
        return;
      }

      const payload = {
        isAuthenticated: true,
        token: String(token || "").trim(),
        email: String(nextEmail || "").trim(),
        role: String(nextRole || "").trim(),
        user: nextUser || null,
        advertisers: Array.isArray(nextAdvertisers) ? nextAdvertisers : [],
        updatedAt: new Date().toISOString(),
      };

      try {
        localStorage.setItem(CLIENT_AUTH_STATE_KEY, JSON.stringify(payload));
      } catch {}
    },
    []
  );

  const runAuthCheck = useCallback(
    async (tokenToCheck) => {
      const tokenKey = String(tokenToCheck || "").trim();

      // ✅ If logout-mode OR recent-logout lock: never re-auth
      if (isLogoutMode() || isLoggingOut() || isInRecentLogoutLock()) {
        setUnauthed();
        return { ok: false, reason: "logout_lock" };
      }

      if (!tokenKey) {
        setUnauthed();
        return { ok: false, reason: "missing_token" };
      }

      if (checkedKeyRef.current === tokenKey) {
        return { ok: true, cached: true };
      }

      if (inflightRef.current?.key === tokenKey && inflightRef.current?.promise) {
        return inflightRef.current.promise;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        try {
          controller.abort();
        } catch {}
      }, AUTH_CHECK_TIMEOUT_MS);
      const requestAuthGeneration = authGenerationRef.current;
      const authCheckWasInvalidated = () =>
        requestAuthGeneration !== authGenerationRef.current ||
        isLogoutMode() ||
        isLoggingOut() ||
        isInRecentLogoutLock() ||
        getStoredTokenClientFirst() !== tokenKey;

      const p = (async () => {
        try {
          setAuthError("");

          const res = await fetch(apiUrl(AUTH_CHECK_ENDPOINT), {
            method: "GET",
            headers: headersForToken(tokenKey),
            signal: controller.signal,
            cache: "no-store",
            // ✅ CRITICAL: do NOT include cookies
            credentials: "omit",
          });

          if (authCheckWasInvalidated()) {
            return { ok: false, reason: "stale_auth_check" };
          }

          if (!res.ok) {
            if (res.status === 401 || res.status === 403) {
              logout();
              return { ok: false, reason: "unauthorized" };
            }

            const text = await res.text().catch(() => "");
            if (authCheckWasInvalidated()) {
              return { ok: false, reason: "stale_auth_check" };
            }
            setUnauthed();
            setAuthError(text || `Auth check failed (${res.status}).`);
            return { ok: false, reason: "auth_check_failed", status: res.status };
          }

          let json = null;
          try {
            json = await res.json();
          } catch {}

          if (authCheckWasInvalidated()) {
            return { ok: false, reason: "stale_auth_check" };
          }

          const explicitOk = computeOkFromJson(json);
          const ok =
            explicitOk !== null ? explicitOk : looksLikeAuthorizedPayload(json);

          if (!ok) {
            setUnauthed();
            persistClientAuthState({ ok: false });
            return { ok: false, reason: "not_authorized", json };
          }

          const nextEmail = String(json?.email || getStoredEmail() || "").trim();
          const nextRole =
            normalizeRole(json?.role || localStorage.getItem("role") || role || "client") ||
            "client";

          // ✅ HARD portal role enforcement
          if (!CLIENT_PORTAL_ROLES.has(nextRole)) {
            logout();
            setAuthError("You do not have access to the Client portal.");
            persistClientAuthState({ ok: false });
            return { ok: false, reason: "wrong_role", json };
          }

          // ✅ NEW: extract user + advertisers for persistence
          const nextUser = extractUserFromPayload(json) || null;
          const nextAdvertisers = extractAdvertisersFromPayload(json);

          if (nextEmail && nextEmail !== email) {
            localStorage.setItem("email", nextEmail);
            localStorage.setItem("userEmail", nextEmail);
            setEmail(nextEmail);
          }

          if (nextRole && nextRole !== role) {
            localStorage.setItem("role", nextRole);
            setRole(nextRole);
          }

          // ✅ NEW: keep user + advertisers in state
          if (nextUser) {
            setUser(nextUser);
            try {
              if (
                typeof nextUser.preferredDisplayName === "string" &&
                nextUser.preferredDisplayName.trim()
              ) {
                localStorage.setItem(
                  "preferredDisplayName",
                  nextUser.preferredDisplayName.trim()
                );
              } else {
                localStorage.removeItem("preferredDisplayName");
              }
            } catch {}
          }
          if (Array.isArray(nextAdvertisers)) setAdvertisers(nextAdvertisers);

          // ✅ Ensure token is stored in client key (even if legacy was used)
          try {
            localStorage.setItem(CLIENT_TOKEN_KEY, tokenKey);
          } catch {}

          setIsAuthenticated(true);
          checkedKeyRef.current = tokenKey;

          // ✅ persist client auth blob
          persistClientAuthState({
            ok: true,
            token: tokenKey,
            nextEmail,
            nextRole,
            nextUser,
            nextAdvertisers,
          });

          try {
            sessionStorage.removeItem(AUTH_TRANSITION_KEY);
          } catch {}

          return { ok: true, json };
        } catch (err) {
          if (authCheckWasInvalidated()) {
            return { ok: false, reason: "stale_auth_check" };
          }
          if (err?.name === "AbortError") {
            setUnauthed();
            setAuthError("Auth check timed out. Please refresh or log in again.");
            persistClientAuthState({ ok: false });
            return { ok: false, reason: "timeout" };
          }

          setUnauthed();
          setAuthError(err?.message || "Auth check failed.");
          persistClientAuthState({ ok: false });
          return { ok: false, reason: "network_error" };
        } finally {
          clearTimeout(timeoutId);
          if (inflightRef.current?.key === tokenKey) {
            inflightRef.current = { key: null, promise: null };
          }
        }
      })();

      inflightRef.current = { key: tokenKey, promise: p };
      return p;
    },
    // AUTH_CHECK_ENDPOINT / AUTH_CHECK_TIMEOUT_MS are module-level constants,
    // not reactive values, so they are deliberately not dependencies.
    [
      email,
      headersForToken,
      logout,
      role,
      setUnauthed,
      persistClientAuthState,
    ]
  );

  const checkAuthentication = useCallback(async () => {
    if (isLogoutMode() || isLoggingOut() || isInRecentLogoutLock()) {
      setUnauthed();
      setIsAuthenticating(false);
      return { ok: false, reason: "logout_lock" };
    }

    // ✅ Client-first token retrieval
    const t = getStoredTokenClientFirst() || authToken || "";

    if (!t) {
      setUnauthed();
      setIsAuthenticating(false);
      return { ok: false, reason: "missing_token" };
    }

    return runAuthCheck(t);
  }, [authToken, runAuthCheck, setUnauthed]);

  // ✅ Initial hydration + auth check (StrictMode-safe)
  useEffect(() => {
    let alive = true;

    (async () => {
      if (isLogoutMode() || isLoggingOut() || isInRecentLogoutLock()) {
        setIsAuthenticating(false);
        setUnauthed();
        return;
      }

      setIsAuthenticating(true);

      // ✅ Client-first token retrieval
      const t = getStoredTokenClientFirst() || authToken || "";
      const e = getStoredEmail() || email || "";
      const r =
        normalizeRole(localStorage.getItem("role") || role || "client") || "client";

      if (t !== authToken) setAuthToken(t);
      if (e !== email) setEmail(e);
      if (r !== role) setRole(r);

      // ✅ NEW: hydrate user/advertisers from persisted blob if present
      try {
        const saved = safeJsonParse(localStorage.getItem(CLIENT_AUTH_STATE_KEY), null);
        if (saved?.isAuthenticated && CLIENT_PORTAL_ROLES.has(normalizeRole(saved?.role))) {
          if (saved.user && !user) setUser(saved.user);
          if (
            Array.isArray(saved.advertisers) &&
            saved.advertisers.length &&
            !advertisers.length
          ) {
            setAdvertisers(saved.advertisers);
          }
        }
      } catch {}

      if (!t) {
        setIsAuthenticated(false);
        setAuthError("");
        setIsAuthenticating(false);
        return;
      }

      const out = await runAuthCheck(t);
      if (!alive) return;

      setIsAuthenticated(Boolean(out?.ok));
      setIsAuthenticating(false);
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // ✅ Keep persisted client auth blob in sync when state changes
  useEffect(() => {
    if (!isAuthenticated || !CLIENT_PORTAL_ROLES.has(normalizeRole(role))) {
      try {
        localStorage.removeItem(CLIENT_AUTH_STATE_KEY);
        localStorage.removeItem("preferredDisplayName");
      } catch {}
      return;
    }

    const payload = {
      isAuthenticated: true,
      email: String(email || "").trim(),
      role: String(role || "").trim(),
      user: user || null,
      advertisers: Array.isArray(advertisers) ? advertisers : [],
      updatedAt: new Date().toISOString(),
    };

    try {
      localStorage.setItem(CLIENT_AUTH_STATE_KEY, JSON.stringify(payload));
      if (typeof user?.preferredDisplayName === "string" && user.preferredDisplayName.trim()) {
        localStorage.setItem("preferredDisplayName", user.preferredDisplayName.trim());
      } else {
        localStorage.removeItem("preferredDisplayName");
      }
    } catch {}
  }, [isAuthenticated, email, role, user, advertisers]);

  const updatePreferredDisplayName = useCallback((nextPreferredDisplayName) => {
    const normalized =
      typeof nextPreferredDisplayName === "string"
        ? nextPreferredDisplayName.trim()
        : "";

    setUser((prev) => {
      if (!prev || typeof prev !== "object") return prev;
      return {
        ...prev,
        preferredDisplayName: normalized || null,
      };
    });

    try {
      if (normalized) {
        localStorage.setItem("preferredDisplayName", normalized);
      } else {
        localStorage.removeItem("preferredDisplayName");
      }
    } catch {}
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticating,
      isAuthenticated,
      authToken,
      email,
      role,
      authError,

      // ✅ NEW exposed (non-breaking)
      user,
      advertisers,

      setSession,
      logout,
      checkAuthentication,
      updatePreferredDisplayName,

      apiUrl,
      apiBase: API_BASE,

      LOGOUT_MODE_KEY,
      LAST_LOGOUT_TS_KEY,

      // ✅ NEW (optional helpers)
      CLIENT_TOKEN_KEY,
      CLIENT_AUTH_STATE_KEY,
    }),
    [
      isAuthenticating,
      isAuthenticated,
      authToken,
      email,
      role,
      authError,
      user,
      advertisers,
      setSession,
      logout,
      checkAuthentication,
      updatePreferredDisplayName,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
